import { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, TextField, Tooltip, Typography } from '@mui/material';
import styles from 'features/task/ui/TaskInfo.module.css';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import Loader from 'shared/loader/Loader';
import { isLoadingCreateMessage, isLoadingTaskInfo, TaskInfo } from 'entities/task-new/selector';
import CustomButton from 'shared/custom-button/CustomButton';
import { useLocation } from 'react-router-dom';
import { fetcEditTask, fetchSendMessage, fetchTaskCase, fetchTaskFiles, fetchTaskMessages } from 'entities/task-new/actions/fetch-task-case';
import ModalWidget from 'widgets/modal/Modal';
import { useForm } from 'shared/utils/hooks/useForm';
import { validationSchema } from '../utils/validationSchema';
import * as Yup from 'yup';
import { acceptedMimeTypes, handleFileChange } from '../utils/handleFileChange';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';

type FileItem = {
    Name: string;
    DataBase64: string;
};

export const TaskInfoComponent: FC = () => {

    const dispatch = useAppDispatch();
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();
    const idTask = query.get('id');

    const isLoadingMessage = useAppSelector(isLoadingCreateMessage);
    const taskInfo = useAppSelector(TaskInfo);
    const isLoading = useAppSelector(isLoadingTaskInfo);

    const [fileError, setFileError] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [open, setOpen] = useState(false);

    const handleApprove = () => {
        if (idTask) {
            dispatch(fetcEditTask({ TaskID: idTask }));
        } else {
            console.error('Missing idCase');
        }
    }
    const formInput = {
        Text: '',
        Files: [],
        TaskID: idTask,
    };

    const {
        formData,
        handleChangeInput,
        errors,
        setErrors,
        handleRemoveFile,
        setFormData
    } = useForm(formInput, validationSchema);

    const textFieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.style.height = 'auto';
            textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
        }
    }, [formData.Text]);

    const handleOpenModalReject = () => {
        setOpen(true);

    }
    const validateForm = async (formData: any) => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });
            return null;
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const errors: { [key: string]: string } = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
                return errors;
            }
            return null;
        }
    };
    const handleReject = async () => {
        if (idTask) {
            const validationErrors = await validateForm(formData);
            if (validationErrors) {
                setErrors(validationErrors);
                return;
            }
            dispatch(fetchSendMessage({
                TaskID: formData.TaskID,
                Files: formData.Files,
                Text: formData.Text,
                ToRework: true
            })).then(() => {
                dispatch(fetchTaskMessages({ TaskID: idTask }));
                setFormData((prev: any) => ({
                    Text: '',
                    Files: [],
                    TaskID: idTask,
                }));
                handleClose();
                dispatch(fetchTaskCase({ TaskID: idTask }));
                dispatch(fetchTaskFiles({ TaskID: idTask }));
            });
        }

    }

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box>
            {isLoading && (
                <Loader
                    title='Загрузка...'
                />
            )}
            {!isLoading && (
                <>
                    <ModalWidget
                        open={open}
                        handleClose={handleClose}
                        title="Уточните, что доработать"
                        closeIconPosition="right"
                        showAcceptButton
                        showCancelButton
                        onAccept={handleReject}
                        onCancel={handleClose}
                        acceptButtonText={!isLoadingMessage ? "Отправить" : "Загрузка..."}
                        widthProps={650}
                    >
                        <Box className={styles.formMessage}>
                            <Box className={styles.inputBox}>
                                <input
                                    accept={acceptedMimeTypes.join(',')}
                                    style={{ display: 'none' }}
                                    id="file-upload"
                                    type="file"
                                    multiple
                                    onChange={(e) => handleFileChange(e, formData, setFormData, setFileError, setIsCompressing)}
                                    disabled={isCompressing}
                                />
                                <label htmlFor="file-upload">
                                    <Tooltip title="Добавить файл" placement="top">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            disabled={isCompressing}
                                        >
                                            {isCompressing ? 'Обработка...' : <AttachFileIcon />}
                                        </Button>
                                    </Tooltip>
                                </label>

                                {fileError && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {fileError}
                                    </Typography>
                                )}
                                <TextField
                                    inputRef={textFieldRef}
                                    label="Текст сообщения"
                                    multiline
                                    rows={1}
                                    value={formData.Text}
                                    name="Text"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeInput({ event })}
                                    variant="outlined"
                                    error={!!errors.Text}
                                    helperText={errors.Text || ''}
                                    sx={{
                                        width: '80%',
                                        '& .MuiInputBase-root': {
                                            height: 'auto',
                                            alignItems: 'flex-start',
                                            minHeight: '40px'
                                        },
                                    }}
                                />
                            </Box>
                            <Box mt={1}>
                                {formData.Files?.map((file: FileItem, index: number) => (
                                    <Chip
                                        key={index}
                                        label={file.Name}
                                        onDelete={() => handleRemoveFile(index)}
                                        deleteIcon={<DeleteIcon />}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    </ModalWidget>

                    <Box className={styles.taskInfo}>
                        <Box className={styles.infoBox}>
                            <Box className={styles.contentRow}>
                                <Box className={styles.contentColumn}><span>Тематика заявки</span><p>{taskInfo.ThemeName}</p></Box>
                                <Box className={styles.contentColumn}><span>Дата подачи заявки</span><p>{taskInfo.Date}</p></Box>
                                <Box className={styles.contentColumn}><span>Ожидаемая дата исполнения</span><p>{taskInfo.AuthorsExpectedCompletionDate ? taskInfo.AuthorsExpectedCompletionDate : '-'}</p></Box>
                            </Box>
                            <Box className={styles.contentRow}>
                                <Box className={styles.contentColumn}><span>Комментарий HR</span><p>{taskInfo.AuthorsComment ? taskInfo.AuthorsComment : '-'}</p></Box>
                            </Box>
                            <Box className={styles.contentRow}>
                                <Box className={styles.contentColumn}><span>Комментарий администратора</span><p>{taskInfo.ResponsiblesComment ? taskInfo.ResponsiblesComment : '-'}</p></Box>
                            </Box>
                        </Box>
                        {taskInfo.AgreementResultName === 'На согласование' && (
                            <Box className={styles.btnBoxChangeStatus}>
                                <CustomButton
                                    title="Согласовать"
                                    handleClick={handleApprove}
                                    textColor="white"
                                    backgroundColor="#0054E6"
                                    gap="10px"
                                    border='none'
                                    borderRadius='4px'
                                    padding="8px 30px"
                                />
                                <CustomButton
                                    title="Доработать"
                                    handleClick={handleOpenModalReject}
                                    padding="8px 30px"
                                    border="none"
                                    textColor="#0054E6"
                                    disabled={isLoading}
                                />
                            </Box>
                        )}


                    </Box>
                </>
            )}
        </Box>
    );
};

export default TaskInfoComponent;
